import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AssMat, AssmatAvailability, AssmatAvailabilityAgrements, AssmatForm, AssmatFormation, AssmatFormationProgramme, MAM } from '@app/models/ass-mat';
import { ApiCrudService } from '.';
import moment from 'moment';
import { ContratAccueilMentalo, EnfantMentalo } from '@app/models/contrat-accueil';
import { AnimationReunion, AnimationReunionPortailEnfantMentalo } from '@app/models/animationReunion';
import { Observable, shareReplay } from 'rxjs';
import { ConfigMentalo } from '@app/models/mentalo';
import { dateInPeriod } from '@app/utils/date-functions';

@Injectable({
  providedIn: 'root'
})
export class AssmatService extends ApiCrudService<AssMat> {

  url = 'assmats';
  formAssmatUrl = 'assmats/#idAssmat#/form/#formPart#';
  formContratAccueilUrl = 'assmats/#idAssmat#/contratAccueil/#idContrat#/form/#formPart#';

  private formationsCache$: Observable<AssmatFormationProgramme>;
  private contratsAccueilCache = new Map<number, Observable<any>>();
  private enfantsMentaloCache = new Map<number, Observable<any>>();

  constructor(
    protected http: HttpClient
  ) {
    super()
  }

  getAssmatById(id: number): Observable<AssmatForm> {
    return this.http.get<AssmatForm>(`${this.url}/${id}`)
  }

  getAssmatVariables(idAssmat: number) {
    return this.http.get(`/assmats/${idAssmat}/summary`);
  }

  getListMAM(idAssmat: number): Observable<MAM[]> {
    return this.http.get<MAM[]>(`/assmats/${idAssmat}/listMAM`);
  }

  getFormData(id, part?) {
    part = part ? part : '';
    const url = this.formAssmatUrl.replace('#idAssmat#', id).replace('#formPart#', part);
    return this.http.get(url, this.httpOptions);
  }

  getFormDataContratAccueilMentalo(idAssmat, idContrat, part?) {
    part = part ? part : '';
    const url = this.formContratAccueilUrl.replace('#idAssmat#', idAssmat).replace('#idContrat#', idContrat).replace('#formPart#', part);

    return this.http.get(url, this.httpOptions);
  }

  update(data: AssMat | any, id?: number, part?: string) {
    part = part ? part : '';
    const url = this.url + (id ? '/' + id : '') + '/' + part;

    return this.http.put(url, data, this.httpOptions);
  }

  updateContratAccueil(data: any, idAssmat: number, id?: number, part?: string) {
    part = part ? part : '';
    const url = this.url + '/' + idAssmat + (id ? '/' + id : '') + (part ? '/' + part : '');

    return this.http.put(url, data, this.httpOptions);
  }

  createContratAccueil(data: any, idAssmat: number) {
    const url = this.url + '/' + idAssmat + '/contratAccueilMentalo';
    return this.http.post(url, data);
  }

  getAvailability(idAssmat: number): Observable<AssmatAvailabilityAgrements> {
    return this.http.get<AssmatAvailabilityAgrements>(`${this.url}/${idAssmat}/disponibilites`)
  }

  saveAvailability(idAssmat: number, listAvailability: AssmatAvailability[]) {
    return this.http.put(`${this.url}/${idAssmat}/disponibilites`, listAvailability, this.httpOptions);
  }

  getFormations(idAssmat: number): Observable<AssmatFormationProgramme> {
    const formationUrl = `${this.url}/${idAssmat}/formations`;

    if (!this.formationsCache$) {
      this.formationsCache$ = this.http.get<AssmatFormationProgramme>(formationUrl).pipe(
        shareReplay(1)
      );
    }
    return this.formationsCache$;
  }

  clearFormationsCache(): void {
    this.formationsCache$ = null;
  }

  // getProgrammesFormation(): Observable<unknown>{
  //   return this.http.get<unknown>(`${this.url}/programmes`)
  // }

  saveFormtaions(idAssmat: number, listFormation: AssmatFormation[]) {
    return this.http.put(`${this.url}/${idAssmat}/formations`, listFormation, this.httpOptions);
  }

  getAgrementsSecondaireByIdAssmat(idAssmat: number) {
    return this.http.get(`${this.url}/${idAssmat}/agrementsSecondaire`)
  }

  getEnfantsMentaloByIdAssMat(idAssmat: number) {
    if (!this.enfantsMentaloCache.has(idAssmat)) {
      const request$ = this.http
        .get(`${this.url}/${idAssmat}/enfantsMentalo`)
        .pipe(
          shareReplay({ bufferSize: 1, refCount: true })
        );

      this.enfantsMentaloCache.set(idAssmat, request$);
    }
    return this.enfantsMentaloCache.get(idAssmat)!;
  }

  // Pour supprimer le cache si besoin
  invalidateEnfantsMentaloCache(idAssmat: number) {
    this.enfantsMentaloCache.delete(idAssmat);
  }

  getContratsAccueilByIdAssMat(idAssmat: number): Observable<ContratAccueilMentalo[]> {
    if (!this.contratsAccueilCache.has(idAssmat)) {
      const request$ = this.http
        .get(`${this.url}/${idAssmat}/contratAccueilMentalo`)
        .pipe(
          shareReplay({ bufferSize: 1, refCount: true })
        );

      this.contratsAccueilCache.set(idAssmat, request$);
    }

    return this.contratsAccueilCache.get(idAssmat)!;
  }

  // Pour supprimer le cache si besoin
  invalidateContratsAccueilCache(idAssmat: number) {
    this.contratsAccueilCache.delete(idAssmat);
  }

  getFamillesMentaloByIdAssMat(idAssmat: number) {
    return this.http.get(`${this.url}/${idAssmat}/famillesMentalo`);
  }

  getEtablissementsEnCoursByIdAssmat(idAssmat: number) {
    return this.http.get(`${this.url}/${idAssmat}/etablissementsMentalo`);
  }

  getTypeEtablissementsEnCoursByIdAssmat(idAssmat: number) {
    return this.http.get(`${this.url}/${idAssmat}/typeEtabsEnCours`);
  }

  getAnimationsByIdAssmatWithProgPerso(idAssmat: number) {
    return this.http.get(`${this.url}/${idAssmat}/animations?withProgPerso=1`);
  }

  getAnimationsByIdAssmat(idAssmat: number) {
    return this.http.get(`${this.url}/${idAssmat}/animations`);
  }

  getReunionsByIdAssmatWithProgPerso(idAssmat: number) {
    return this.http.get(`${this.url}/${idAssmat}/reunions?withProgPerso=1`);
  }

  getReunionsByIdAssmat(idAssmat: number) {
    return this.http.get(`${this.url}/${idAssmat}/reunions`);
  }

  inscriptionAnimationReunion(idAssmat: number, data: any) {
    return this.http.put(`${this.url}/${idAssmat}/animationsReunions/inscription`, data);
  }

  desinscriptionAnimationReunion(idAssmat: number, data: any) {
    return this.http.put(`${this.url}/${idAssmat}/animationsReunions/desinscription`, data);
  }

  desinscriptionChildAnimationReunion(idAssmat: number, idEnfantMentalo: number, data: any) {
    return this.http.put(`${this.url}/${idAssmat}/animationsReunions/${idEnfantMentalo}/desinscription`, data);
  }

  inscriptionChildAnimationReunion(idAssmat: number, data: any) {
    return this.http.post(`${this.url}/${idAssmat}/animationsReunions/enfants/inscription`, data);
  }

  //Fonctions utilisées dans les composants animation et reunion

  checkAssmatRegistered(listItem: AnimationReunion[], idAssmat: number) {
    listItem.forEach(item => {
      if (item.tabAssmats) {
        item.tabAssmats.forEach(assmat => {
          if (assmat.idParticipant === idAssmat) {
            item.assmatIsRegistered = true;
          };
        })
      }
      if (item.portailInscription && item.portailInscription.idAssmat === idAssmat) {
        item.assmatIsRegistered = true;
      }
    })
  }

  checkChildRegistered(listChild: EnfantMentalo[], listItem: AnimationReunion[]) {
    listItem.forEach(item => {
      item.listEnfantMentaloRegisteredByAssmat = [];

      if (item.portailInscription) {
        item.portailInscription._EnfantsMentalo.forEach((childRegistered: AnimationReunionPortailEnfantMentalo) => {
          listChild.forEach(child => {
            if (childRegistered.idEnfantMentalo === child.id) {
              if (!this.childExists(child.id, item.listEnfantMentaloRegisteredByAssmat)) {
                let EnfantAnimationReunion = {
                  child,
                  idPortailAnimationReunionEnfant: childRegistered.idPortailAnimationReunionEnfant,
                  typeDemande: childRegistered.typeDemande,
                  decision: childRegistered.decision,
                  motifRefus: childRegistered.motifRefus
                }
                item.listEnfantMentaloRegisteredByAssmat.push(EnfantAnimationReunion);
              }
            }
          })
        })
      }
      if (item.tabEnfantsMentalo) {
        item.tabEnfantsMentalo.forEach(childRegistered => {
          listChild.forEach(child => {
            if (childRegistered.idEnfantMentalo === child.id) {
              if (!this.childExists(child.id, item.listEnfantMentaloRegisteredByAssmat)) {
                item.listEnfantMentaloRegisteredByAssmat.push({ child });
              }
            }
          })
        })
      }
    })
  }

  getChildListWithContratEnable(contratsList: ContratAccueilMentalo[], childList: EnfantMentalo[], dateAnimationreunion: string | Date) {
    let listChildWithContratEnabled = [];
    contratsList = contratsList.filter(contrat => dateInPeriod(contrat.dateDebut, contrat.dateFin, dateAnimationreunion));

    contratsList.forEach(contrat => {
      childList.forEach(children => {
        if (contrat.idEnfantMentalo === children.id) {
          listChildWithContratEnabled.push(children);
        }
      })
    });
    return listChildWithContratEnabled;
  }

  // checkChilRegisteredInAnimation(listChild: EnfantMentalo[], listItem: Reunion[] | Animation[]) {
  //   listItem.forEach(item => {
  //     item.listEnfantMentaloRegisteredByAssmat = [];
  //     item.tabEnfantsMentalo.forEach(childRegistered => {
  //       listChild.forEach(childAssmat => {
  //         if (childRegistered.idParticipant === childAssmat.id || childRegistered.idEnfantMentalo === childAssmat.id) {
  //           if (!this.childExists(childAssmat.id, item.listEnfantMentaloRegisteredByAssmat)) {
  //             item.listEnfantMentaloRegisteredByAssmat.push(childAssmat);
  //           }
  //         }
  //       })
  //     })
  //   })
  // }

  // checkChildRegisteredInInscriptionAnimation(listChild: EnfantMentalo[], listItem: Animation[] | Reunion[]) {
  //   listItem.forEach(item => {
  //     item.listEnfantMentaloRegisteredByAssmat = [];
  //     item.portailAnimation._EnfantsMentalo.forEach(childRegistered => {
  //       listChild.forEach(child => {
  //         if (childRegistered.idEnfantMentalo === child.id) {
  //           if (!this.childExists(child.id, item.listEnfantMentaloRegisteredByAssmat)) {
  //             item.listEnfantMentaloRegisteredByAssmat.push(child);
  //           }
  //         }
  //       })
  //     })
  //   })
  // }

  childExists(idChild, listChild) {
    return listChild.some(function (el) {
      return el.child.id === idChild;
    });
  }

  sortByDate(listItem: AnimationReunion[]) {
    return listItem.sort((a, b) => {
      const dateA = moment(`${a.date} ${a.heureDebut}`, 'YYYY-MM-DD HH:mm');
      const dateB = moment(`${b.date} ${b.heureDebut}`, 'YYYY-MM-DD HH:mm');
      return dateA.diff(dateB);
    });
  }

  filtrerAnimationsReunionAvailables(animationReu: AnimationReunion[]) {
    const now = moment();

    return animationReu.filter(animReu => {
      const dateAnimReu = moment(animReu.date, "YYYY-MM-DD");
      const heureDebutAnimReu = moment(animReu.heureDebut, "HH:mm");

      if (dateAnimReu.isAfter(now, 'day')) {
        return true;
      }

      if (dateAnimReu.isSame(now, 'day')) {
        return heureDebutAnimReu.isAfter(now, 'minute');
      }

      return false;
    });
  }

  canRegisterMore(list: AnimationReunion[], type: 'animation' | 'reunion', configMentalo: ConfigMentalo): boolean {
    const listFiltered = this.filtrerAnimationsReunionAvailables(list)
    const enAttenteCount = listFiltered
      .filter(animReu => (this.getStatutAnimReu(animReu) === 'EnAttente') && animReu.portailInscription.typeDemande === 'Inscription')
      .length;

    const maxAllowed = type === 'animation'
      ? configMentalo.nbMaxInscriprionAnimEnAttente
      : type === 'reunion'
        ? configMentalo.nbMaxInscriprionReuEnAttente
        : null;

    return maxAllowed !== null && maxAllowed !== undefined
      ? enAttenteCount < maxAllowed
      : true;
  }

  getStatutAnimReu(animationReunion: AnimationReunion): string {
    if (animationReunion.portailInscription) {
      return animationReunion.portailInscription.decision;
    } else return "Acceptee";
  }

}

